/*
 * File: CustomerSelectionCard.tsx
 * Project: papa-bear
 * -----
 * Last Modified: 09 Dec 2021 4:34:13 pm
 * -----
 * Copyright (c) 2021 Elegant Software Solutions All rights reserved.
 */

import { Card, Col, Row, Typography } from 'antd';
import { useFormikContext } from 'formik';
import { CustomerNameModel } from 'models/CustomerNameModel';
import { InlineStylesModel } from 'models/InlineStylesModel';
import { SalesOrderPayloadModel } from 'models/SalesOrderPayloadModel';
import { useDispatch } from 'react-redux';
import { useParams } from 'react-router-dom';
import { setSelectedCustomerAccount } from 'redux/app/app-slice';
import { setFilterCustomerId, setFilterCustomerName } from 'redux/salesOrder/customerSelectionFiltersSlice';

/* ****************** Interfaces ****************** */
interface CustomerSelectionCardProps {
  customer: CustomerNameModel;
}

const styles: InlineStylesModel = {
  cardBody: {
    // padding: 10,
    // paddingLeft: 15
    borderRadius: 10,
    padding: 0
  },
  customerInfo: {
    padding: '10px 60px'
  },
  title: {
    fontSize: '.8rem',
    margin: 0,
    color: 'rgba(0, 0, 0, .4)'
  },
  titleWhite: {
    fontSize: '.8rem',
    margin: 0,
    color: 'rgba(255, 255, 255, .4)'
  },
  values: {
    fontSize: 16,
    fontWeight: 600
  },
  valuesWhite: {
    color: 'rgba(255, 255, 255, .9)',
    fontSize: 16,
    fontWeight: 600
  },
  minusButton: {
    color: '#F93943',
    borderColor: '#F93943'
  },
  selectedStyles: {
    transition: '.25s all ease-out',
    background: '#4e937aab',
    boxShadow: '0 0 6px rgba(0,0,0,.4)',
    borderRadius: 10
  }
};

export const CustomerSelectionCard = ({ customer }: CustomerSelectionCardProps): JSX.Element => {

  const { id: salesOrderId } = useParams();
  const isDraftForm = Boolean(salesOrderId);
  const { values, setValues } = useFormikContext<SalesOrderPayloadModel>();

  const dispatch = useDispatch();

  /* ****************** Functions ****************** */

  const handleAddCustomer = (): void => {
    console.log(customer, 'customer ')
    dispatch(setSelectedCustomerAccount(customer.customerAccount))
    if (values.customerId !== customer.customerAccount && !isDraftForm) {
      setValues((prevState) => ({
        ...prevState,

        buyerId: '',
        buyerName: '',
        customerName: customer.name,
        customerId: customer.customerAccount,
        siteId: customer.siteId || '',
        warehouseId: customer.warehouseId || '',

        freightTerms: customer.freightTerm || '',
        modeOfDelivery: customer.modeOfDelivery || '',
        salesPersonFullName: '',
        salesPersonPersonnelNumber: '',
        deliveryAddressStreet: '',
        deliveryAddressCity: '',
        deliveryAddressCounty: '',
        deliveryAddressState: '',
        deliveryAddressZipCode: '',
        deliveryAddressDescription: '',
        deliveryAddressLocationId: ''
      }));
    }
  };

  const handleRemoveCustomer = (): void => {
    dispatch(setFilterCustomerId(undefined));
    dispatch(setFilterCustomerName(undefined));
    if (values.deliveryAddressLocationId) {
      setValues((prevState) => ({
        ...prevState,
        buyerId: '',
        buyerName: '',
        customerName: '',
        customerId: '',
        siteId: '',
        warehouseId: '',
        freightTerms: '',
        modeOfDelivery: '',
        salesPersonFullName: '',
        salesPersonPersonnelNumber: '',
        deliveryAddressStreet: '',
        deliveryAddressCity: '',
        deliveryAddressCounty: '',
        deliveryAddressState: '',
        deliveryAddressZipCode: '',
        deliveryAddressDescription: '',
        deliveryAddressLocationId: ''
      }));
    } else {
      setValues((prevState) => ({
        ...prevState,
        buyerId: '',
        buyerName: '',
        customerName: '',
        customerId: '',
        siteId: '',
        warehouseId: '',
        freightTerms: '',
        modeOfDelivery: '',
        salesPersonFullName: '',
        salesPersonPersonnelNumber: ''
      }));
    }

    // dispatch(setCustomerSelectionOffset(0));
  };

  /* ****************** JSX / Variables ****************** */
  const cardStyle = values.customerId && values.customerId === customer.customerAccount ? styles.selectedCustomer : styles.cardBody;
  const isSelectedCard = values.customerId === customer.customerAccount;

  /* ****************** Render ****************** */
  return (
    <Card
      bordered={false}
      style={isSelectedCard ? styles.selectedStyles : cardStyle}
      bodyStyle={styles.cardBody}
      onClick={isSelectedCard && !isDraftForm ? handleRemoveCustomer : handleAddCustomer}
      hoverable={!isDraftForm}>
      <Row style={{ borderRadius: 10 }}>
        <Col span={6} style={{ background: isSelectedCard ? '#4e937a' : 'rgba(0,0,0,.2)', padding: '10px 20px', borderRadius: '10px 0 0 10px' }}>
          <Typography.Paragraph style={isSelectedCard ? styles.titleWhite : styles.title}>Account</Typography.Paragraph>
          <Typography.Paragraph style={isSelectedCard ? styles.valuesWhite : styles.values} ellipsis={{ tooltip: `${customer.customerAccount}` }}>
            {customer.customerAccount}
          </Typography.Paragraph>
        </Col>
        <Col span={16}>
          <div
            style={{
              position: 'absolute',
              left: 0,
              top: '50%',
              transform: 'translateY(-50%)',
              width: '80px',
              height: '40px',
              borderLeft: isSelectedCard ? 'solid 40px #4e937a' : 'solid 40px rgba(0,0,0,.2)',
              borderBottom: 'solid 40px transparent',
              borderTop: 'solid 40px transparent'
            }}
          />
          <Col style={styles.customerInfo}>
            <Typography.Paragraph style={isSelectedCard ? styles.titleWhite : styles.title}>Customer Name</Typography.Paragraph>
            <Typography.Paragraph style={isSelectedCard ? styles.valuesWhite : styles.values} ellipsis={{ tooltip: `${customer.name}` }}>
              {customer.name}
            </Typography.Paragraph>
          </Col>
        </Col>
      </Row>
    </Card>
  );
};
